import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import {
  cgtBullet,
  cgtHeadline,
  DISCLAIMER,
  formatAud,
  sourceBullet,
  twelveMonthBullet,
  washSaleBullet,
  washSaleHeadline,
} from "@aucash/rules/cgt";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, MoneyInput } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular, webNoOutline } from "../../src/theme";

// cgt — Capital Gains Tax on a sale, surfaced stateless-v1 (PLAN arch decision
// #1). One screen: a cost-base + dates + other-income form runs the `on-sale`
// surface; an optional, clearly-separated wash-sale check runs the `wash-sale`
// surface. Both are stateless `calc.cgt.compute` queries — input -> output, no
// save-by-id, no auth gate, no persistence. The result renders INLINE.
//
// Copy comes verbatim from the rules layer's copy helpers so the wording can
// never drift from the API/SEO surfaces (validation §3: information, not advice;
// no "sell now to save tax", no broker/exchange handoff — the rules-layer
// `assertNoFinancialProductHandoff` guard already strips any such term from the
// output we render, and the wash-sale read is a NEUTRAL warning, never a
// clearance).

const ASSESSMENT_YEAR = "2025-26";
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

type OnSaleResult = Awaited<ReturnType<typeof trpc.calc.cgt.compute.query>>;
type WashSaleResult = Awaited<ReturnType<typeof trpc.calc.cgt.compute.query>>;

export default function CgtIndex(): JSX.Element {
  const router = useRouter();

  // --- on-sale form ---
  const [proceeds, setProceeds] = useState("");
  const [acquisitionCost, setAcquisitionCost] = useState("");
  const [incidentalCosts, setIncidentalCosts] = useState("");
  const [capitalImprovementCosts, setCapitalImprovementCosts] = useState("");
  const [acquisitionContractDate, setAcquisitionContractDate] = useState("");
  const [disposalContractDate, setDisposalContractDate] = useState("");
  const [otherTaxableIncome, setOtherTaxableIncome] = useState("");
  const [hasStudyLoan, setHasStudyLoan] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<OnSaleResult | null>(null);

  // --- wash-sale check (optional, separate) ---
  const [soldAtLoss, setSoldAtLoss] = useState(false);
  const [reacquired, setReacquired] = useState(false);
  const [dominantPurpose, setDominantPurpose] = useState(false);
  const [washSubmitting, setWashSubmitting] = useState(false);
  const [washResult, setWashResult] = useState<WashSaleResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (proceeds.trim() === "")
      next.proceeds = "Enter the sale price (capital proceeds).";
    if (acquisitionCost.trim() === "")
      next.acquisitionCost = "Enter what you paid for the asset.";
    if (!ISO_DATE_RE.test(acquisitionContractDate.trim()))
      next.acquisitionDate = "Enter the purchase contract date as YYYY-MM-DD.";
    if (!ISO_DATE_RE.test(disposalContractDate.trim()))
      next.disposalDate = "Enter the sale contract date as YYYY-MM-DD.";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0 || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.cgt.compute
      .query({
        input: {
          surface: "on-sale",
          assetClass: "shares",
          proceeds: num(proceeds),
          costBase: {
            acquisitionCost: num(acquisitionCost),
            incidentalCosts: num(incidentalCosts),
            capitalImprovementCosts: num(capitalImprovementCosts),
          },
          acquisitionContractDate: acquisitionContractDate.trim(),
          disposalContractDate: disposalContractDate.trim(),
          otherTaxableIncome: num(otherTaxableIncome),
          hasStudyLoan,
          assessmentYear: ASSESSMENT_YEAR,
        },
      })
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const onWashCheck = (): void => {
    if (washSubmitting) return;
    setWashSubmitting(true);
    setWashResult(null);
    trpc.calc.cgt.compute
      .query({
        input: {
          surface: "wash-sale",
          soldAtLoss,
          reacquiredSameOrSubstantiallyIdentical: reacquired,
          dominantPurposeTaxBenefit: dominantPurpose,
        },
      })
      .then((res) => setWashResult(res))
      .catch(() => setWashResult(null))
      .finally(() => setWashSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Capital gains tax" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>FY26 · capital gains tax</Eyebrow>
            <Text style={styles.title}>
              What's the CGT on selling an asset?
            </Text>
            <Text style={styles.subtitle}>
              Estimate the capital gain on selling shares, property or crypto —
              the five-element cost base, the 50% discount for assets held more
              than 12 months, and the marginal-rate tax the net gain adds on top
              of your other income, from the current ATO rules.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field
            label="Sale price (capital proceeds)"
            help="What you sold the asset for — the proceeds from the disposal."
            error={errors.proceeds}
          >
            <MoneyInput
              value={proceeds}
              onChange={setProceeds}
              testID="input-proceeds"
              accessibilityLabel="Sale price (capital proceeds)"
            />
          </Field>

          <Field
            label="Purchase price (what you paid)"
            help="Element 1 of the cost base — the money paid to acquire the asset."
            error={errors.acquisitionCost}
          >
            <MoneyInput
              value={acquisitionCost}
              onChange={setAcquisitionCost}
              testID="input-acquisition-cost"
              accessibilityLabel="Purchase price"
            />
          </Field>

          <Field
            label="Buying & selling costs (optional)"
            help="Brokerage, stamp duty, legal, conveyancing, agent commission — incidental costs to acquire and dispose."
          >
            <MoneyInput
              value={incidentalCosts}
              onChange={setIncidentalCosts}
              accessibilityLabel="Buying and selling costs"
            />
          </Field>

          <Field
            label="Capital improvement costs (optional)"
            help="Capital spending that improved the asset's value (e.g. a renovation)."
          >
            <MoneyInput
              value={capitalImprovementCosts}
              onChange={setCapitalImprovementCosts}
              accessibilityLabel="Capital improvement costs"
            />
          </Field>

          <Field
            label="Purchase contract date"
            help="The CONTRACT date you bought (not settlement), as YYYY-MM-DD. The 12-month discount test runs contract-to-contract."
            error={errors.acquisitionDate}
          >
            <DateInput
              value={acquisitionContractDate}
              onChange={setAcquisitionContractDate}
              testID="input-acquisition-date"
              accessibilityLabel="Purchase contract date"
            />
          </Field>

          <Field
            label="Sale contract date"
            help="The CONTRACT date you sold (not settlement), as YYYY-MM-DD."
            error={errors.disposalDate}
          >
            <DateInput
              value={disposalContractDate}
              onChange={setDisposalContractDate}
              testID="input-disposal-date"
              accessibilityLabel="Sale contract date"
            />
          </Field>

          <Field
            label="Your other taxable income for the year (optional)"
            help="Salary and other taxable income. The net gain is taxed at your marginal rate on top of this — never in isolation."
          >
            <MoneyInput
              value={otherTaxableIncome}
              onChange={setOtherTaxableIncome}
              accessibilityLabel="Your other taxable income for the year"
            />
          </Field>

          <Field label="Do you have a HELP / study loan?">
            <Checkbox
              checked={hasStudyLoan}
              onChange={setHasStudyLoan}
              label="Yes — I have an outstanding HECS-HELP or other study and training loan."
            />
          </Field>

          <Button
            label={submitting ? "Calculating…" : "See your estimate →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="cgt-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}

          {/* Wash-sale check — a NEUTRAL informational warning surface. Kept
              clearly separate from the tax estimate. Never a clearance. */}
          <View style={styles.washStage}>
            <Text style={styles.sectionTitle}>
              Wash-sale check (optional)
            </Text>
            <Text style={styles.bodyText}>
              If you sold at a loss and plan to buy back the same or a
              substantially identical asset, the ATO's wash-sale rule may apply.
              This is information about the rule, not a clearance — there is no
              fixed time window.
            </Text>
            <Field label="Did this sale crystallise a capital loss?">
              <Checkbox
                checked={soldAtLoss}
                onChange={setSoldAtLoss}
                label="Yes — I sold the asset for less than its reduced cost base."
              />
            </Field>
            <Field label="Do you intend to reacquire the same (or a substantially identical) asset?">
              <Checkbox
                checked={reacquired}
                onChange={setReacquired}
                label="Yes — I plan to buy back the same or a substantially identical asset."
              />
            </Field>
            <Field label="Was the dominant purpose of selling to generate the tax loss?">
              <Checkbox
                checked={dominantPurpose}
                onChange={setDominantPurpose}
                label="Yes — generating the tax loss was the main reason for selling."
              />
            </Field>
            <Button
              label={washSubmitting ? "Checking…" : "Check the wash-sale rule →"}
              onPress={onWashCheck}
              disabled={washSubmitting}
              full
              testID="cgt-wash-submit"
            />
            {washResult !== null ? <WashBlock result={washResult} /> : null}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline on-sale result — stateless, rendered straight under the form. The
 * full disclaimer (rules-layer DISCLAIMER) sits at the TOP, before any number
 * (validation §3.2 placement rule). */
function ResultBlock({ result }: { result: OnSaleResult }): JSX.Element {
  const out = result.output as Extract<typeof result.output, { costBase: number }>;

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your estimate · FY26</Eyebrow>
        <Chip label="Estimate" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{cgtHeadline(out)}</Text>
        <Text style={styles.bodyText}>{cgtBullet(out)}</Text>
        {out.grossCapitalGain > 0 ? (
          <Text style={styles.bodyText}>{twelveMonthBullet(out)}</Text>
        ) : null}
      </Card>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          {sourceBullet(result.ruleVersion, result.sourcesVerifiedAt)}
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** Inline wash-sale read — a NEUTRAL warning, never a clearance. Uses the
 * rules-layer copy verbatim. */
function WashBlock({ result }: { result: WashSaleResult }): JSX.Element {
  const out = result.output as Extract<typeof result.output, { risk: string }>;
  return (
    <View style={styles.washResult}>
      <Card style={{ gap: space.sm }}>
        <Text style={styles.washLead}>{washSaleHeadline(out)}</Text>
        <Text style={styles.bodyText}>{washSaleBullet(out)}</Text>
        <SourceLink
          label={result.sources.washSaleTA20087 ?? ""}
          url={result.sources.washSaleTA20087}
        />
      </Card>
    </View>
  );
}

/** Plain ISO-date text input, styled to match MoneyInput. The rules engine
 * takes contract dates as yyyy-mm-dd strings. */
function DateInput({
  value,
  onChange,
  testID,
  accessibilityLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  testID?: string;
  accessibilityLabel?: string;
}): JSX.Element {
  return (
    <TextInput
      value={value}
      onChangeText={onChange}
      placeholder="YYYY-MM-DD"
      placeholderTextColor={colors.muted}
      autoCapitalize="none"
      autoCorrect={false}
      keyboardType="numbers-and-punctuation"
      style={styles.dateInput}
      testID={testID}
      accessibilityLabel={accessibilityLabel}
    />
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined || url === "") return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

// `formatAud` is imported for parity with the other screens' result helpers; the
// headline strings already embed the formatted figures, so it isn't called
// directly here. Referenced to keep the import meaningful without a no-op.
void formatAud;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  dateInput: {
    fontFamily: fonts.bodyMedium,
    fontSize: 16,
    color: colors.ink,
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    paddingVertical: space.md,
    paddingHorizontal: space.lg,
    ...webNoOutline,
  },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },

  washStage: { gap: space.lg, marginTop: space.md },
  washResult: { marginTop: space.sm },
  washLead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 20,
    lineHeight: 26,
    letterSpacing: -0.4,
    color: colors.ink,
  },
});
